import { Frequency } from "domain/workflows";

type DayOfWeek = "MON" | "TUE" | "WED" | "THU" | "FRI" | "SAT" | "SUN";

const DAY_ORDER: DayOfWeek[] = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

interface ScheduleFormValues {
    launchSchedule: Frequency;
    everyHours: number;
    launchTimeHours: string | number;
    launchTimeMinutes: string | number;
    timezone: string;
    daysOfWeek: DayOfWeek[];
    dayOfMonth: number;
}

const padTime = (value: string | number): string => String(value).padStart(2, "0");

const getOrdinalSuffix = (day: number): string => {
    if (day >= 11 && day <= 13) {
        return "th";
    }
    switch (day % 10) {
        case 1:
            return "st";
        case 2:
            return "nd";
        case 3:
            return "rd";
        default:
            return "th";
    }
};

export const buildScheduleSummary = (
    values: ScheduleFormValues,
    t: (key: string, options?: Record<string, unknown>) => string,
    lastDayValue: number = -1
): string => {
    const time = `${padTime(values.launchTimeHours)}:${padTime(values.launchTimeMinutes)}`;

    if (values.launchSchedule === "WEEKLY") {
        const days = [...values.daysOfWeek]
            .sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b))
            .map((day) => t(`ScheduleWorkflow.dayAbbreviations.${day}`))
            .join(", ");
        return t("ScheduleWorkflow.summary.weekly", { days, time, timezone: values.timezone });
    }

    if (values.launchSchedule === "DAILY") {
        return t("ScheduleWorkflow.summary.daily", { time, timezone: values.timezone });
    }

    if (values.launchSchedule === "MONTHLY") {
        const dayOfMonth =
            values.dayOfMonth === lastDayValue
                ? t("ScheduleWorkflow.last").toLowerCase()
                : `${values.dayOfMonth}${getOrdinalSuffix(values.dayOfMonth)}`;
        return t("ScheduleWorkflow.summary.monthly", { dayOfMonth, time, timezone: values.timezone });
    }

    const hourlyKey =
        values.everyHours === 1 ? "ScheduleWorkflow.summary.hourlyOne" : "ScheduleWorkflow.summary.hourlyOther";
    return t(hourlyKey, { hours: values.everyHours, timezone: values.timezone });
};

export const formatDaysOfWeek = (days: DayOfWeek[], t: (key: string) => string): string =>
    [...days]
        .sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b))
        .map((day) => t(`ScheduleWorkflow.dayNames.${day}`))
        .join(", ");

export { padTime, getOrdinalSuffix };
export type { ScheduleFormValues, DayOfWeek };
